import {
  faEdit,
  faSave,
  faTimes,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "components/base/Button";
import ReactSelect from "components/base/ReactSelect";
import axiosInstance from "helpers/api";
import { useEffect, useState } from "react";
import { useAtom } from "jotai";
import {
  ButtonGroup,
  Card,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
  ToggleButton,
} from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { MultiValue } from "react-select";
import { dealsAtom } from "jotai/LeadsJotaiInstance";
import { useParams } from "react-router-dom";

const radios = [
  { name: "FLAT", value: "flat" },
  { name: "%", value: "percentage" },
];

type Inputs = {
  deal_name: string;
  deal_email: string;
  deal_mls_id: string;
  deal_commission_type: string;
  deal_commission: number;
  deal_price: string;
  deal_price_type: string;
  deal_note: string;
  deal_closing_date: Date;
  deal_inspection_period: number;
  deal_city: string;
  deal_state: string;
  deal_lead_type: string;
  deal_lead_status: string;
  deal_zip: string;
  deal_stage_id: number;
  customers: [];
  collaborators: [];
  related_parties: [];
};

type LeadOption = {
  value: string;
  label: string;
};

// Define the type for related party objects
interface RelatedParty {
  lead: {
    label: string;
    value: string;
  };
  role: string;
}

const EditDealModal = ({ show, handleClose, setDealDetails }: any) => {
  const initialPricing = {
    discount_amount: 0,
    discount_price: 0,
    discount_type: "flat",
  };

  const [price, setPrice] = useState(initialPricing);
  const [commissionType, setCommissionType] = useState("flat");
  const [commissionValue, setCommissionValue] = useState("");
  const [showInputForm, setShowInputForm] = useState(false);
  const [initialPrice, setInitialPrice] = useState("");
  const [relatedParties, setRelatedParties] = useState<RelatedParty[]>([]);
  const [leads, setLeads] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState<MultiValue<LeadOption>>(
    []
  );
  const [collaborators, setCollaborators] = useState([]);
  const [selectedCollaborators, setSelectedCollaborators] = useState<
    MultiValue<LeadOption>
  >([]);
  const [relatedPropertiesLead, setRelatedPartiesLead] = useState({
    label: "",
    value: "",
  });
  const [relatedPropertiesRole, setRelatedPartiesRole] = useState("");
  const [deals, setDeals] = useAtom(dealsAtom);
  const [loading, setLoading] = useState(false);

  const [leadType, setLeadType] = useState<string | undefined>(undefined);
  const [leadStatus, setLeadStatus] = useState<any>(null);
  const [leadTypes, setLeadTypes] = useState([]);
  const [selectedLeadStatus, setSelectedLeadStatus] = useState([]);
  const [typeError, setTypeError] = useState(false);
  const [statusError, setStatusError] = useState(false);

  const fetchLeadTypes = async () => {
    try {
      const response = await axiosInstance.get("/leads/types");
      setLeadTypes(response.data.data.leadTypes);
    } catch (error: any) {
      toast.error(error.response.data.message);
    }
  };

  const matchStatus = async (id: any) => {
    setLeadType(id);

    try {
      const response = await axiosInstance.get(`/leads/status/${id}`);
      setSelectedLeadStatus(response.data.data.leadTypes);
    } catch (error: any) {
      toast.error(error.response.data.message);
    }
  };
  const handleSelectStatus = (e: any) => {
    setLeadStatus(e);
  };

  useEffect(() => {
    fetchLeadTypes();
  }, []);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<Inputs>();

  let { id } = useParams();
  const fetchDealData = async () => {
    try {
      const response = await axiosInstance.get(`/deals/${id}`);
      const dealData = response.data?.data?.deal || response.data?.deal;

      if (!dealData) {
        toast.error("Deal data not found in response");
        return;
      }
      setRelatedParties(dealData.related_deals || []);

      const formattedCollaborators = dealData.collaborators.map(
        (lead: any) => ({
          value: lead.id,
          label: `${lead.collaborator_first_name}`,
        })
      );

      const formattedCustomers = dealData.customers.map((lead: any) => ({
        value: lead.id,
        label: `${lead.lead_first_name}`,
      }));

      setSelectedCollaborators(formattedCollaborators || []);
      setSelectedLeads(formattedCustomers || []);
      setValue("deal_name", dealData.deal_name || "");
      setValue("deal_city", dealData.deal_city || "");
      setValue("deal_state", dealData.deal_state || "");
      setValue(
        "deal_closing_date",
        dealData.deal_closing_date?.split("T")[0] || ""
      );
      setValue("deal_commission", dealData.deal_commission || 0);
      setValue("deal_commission_type", dealData.deal_commission_type || "flat");
      setValue("deal_mls_id", dealData.deal_mls_id || "");
      setValue("deal_inspection_period", dealData.deal_inspection_period || 0);
      setValue("deal_lead_type", dealData.deal_lead_type || "");
      setValue("deal_lead_status", dealData.deal_lead_status || "");
      setValue("deal_note", dealData.deal_note || "");
      setValue("deal_price", dealData.deal_price || "");
      setValue("deal_price_type", dealData.deal_price_type || "");
      setValue("deal_zip", dealData.deal_zip || "");
      setValue("deal_stage_id", dealData.deal_stage_id || "");
      setValue("deal_state", dealData.deal_state || "");
      setValue("deal_city", dealData.deal_city || "");
      setCommissionType(dealData.deal_commission_type || "flat");
      setCommissionValue(dealData.deal_commission?.toString() || "0");
      setInitialPrice(dealData.deal_price || "0");
      setLeadType(dealData.lead_type_id);

      const getTypes = await axiosInstance.get(
        `/leads/status/${dealData.lead_type_id}`
      );

      setSelectedLeadStatus(getTypes.data.data.leadTypes);
      const filterTypes = getTypes.data.data.leadTypes.find(
        (item: any) => item.id === dealData.lead_type_status_id
      );

      setLeadStatus(filterTypes.id);
      // setPrice({
      //   discount_amount: dealData.deal_commission || 0,
      //   discount_price: dealData.deal_price || 0,
      //   discount_type: dealData.deal_commission_type || "flat",
      // });

      setLoading(false);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch deal data";
      toast.error(errorMessage);
      console.error("Deal fetch error:", error);
    }
  };

  const fetchLeadData = async () => {
    try {
      const response = await axiosInstance.get("/deals");
      setDeals(response.data.data);
    } catch (error: any) {
      console.log(error.response.data.message);
    }
  };

  useEffect(() => {
    fetchDealData();
    fetchLeadData();
  }, []);

  const getISODate = (date: Date) => {
    try {
      return date ? new Date(date).toISOString() : null;
    } catch {
      return null;
    }
  };

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    if (!leadType) {
      setTypeError(true);
      return;
    }
    if (!leadStatus) {
      setStatusError(true);
      return;
    }
    setLoading(true);
    const related_parties_new = relatedParties.map((party: any) => ({
      id: party.lead.value ? party.lead.value : party.id,
      role: party.role,
    }));

    const payload = {
      deal_name: data.deal_name,
      deal_email: data.deal_email || "",
      deal_mls_id: data.deal_mls_id,
      deal_inspection_period: data.deal_inspection_period,
      deal_commission: +commissionValue,
      deal_closing_date: getISODate(data.deal_closing_date),
      deal_note: data.deal_note,
      deal_price: price.discount_price ? price.discount_price : +initialPrice,
      deal_price_type: data.deal_price_type,
      deal_city: data.deal_city,
      deal_state: data.deal_state,
      deal_commission_type: commissionType,
      lead_type_id: +(leadType ?? 0),
      lead_type_status_id: +leadStatus,
      deal_stage_id: +data.deal_stage_id,
      deal_zip: data.deal_zip,
      customers: selectedLeads.map((lead) => lead.value),
      collaborators: selectedCollaborators.map((collab) => collab.value),
      related_deals: related_parties_new,
    };

    try {
      const response = await axiosInstance.patch(`/deals/${id}`, payload);
      toast.success(response.data.message);
      reset();
      setDealDetails(response.data.data);
      setLoading(false);
      handleClose();
      fetchDealData();
    } catch (error: any) {
      toast.error(error.response.data.message);
      setLoading(false);
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await axiosInstance.get("/leads");
      const leadsName = response.data.data.leads.map(
        (lead: any, index: any) => {
          return { label: lead.lead_first_name, value: lead.id };
        }
      );

      setLeads(leadsName);
    } catch (error: any) {
      toast.error(error.response.data.message);
    }
  };

  const fetchCollaborators = async () => {
    try {
      const response = await axiosInstance.get("/collaborators");
      const collaboratorsName = response.data.data.collaborators.map(
        (collaborator: any, index: any) => {
          return {
            label: collaborator.invitee.collaborator_first_name,
            value: collaborator.collaborator_id,
          };
        }
      );
      setCollaborators(collaboratorsName);
    } catch (error: any) {
      toast.error(error.response.data.message);
    }
  };

  useEffect(() => {
    fetchLeads();
    fetchCollaborators();
  }, []);

  const handleCommissionValue = (e: any) => {
    setCommissionValue(e);
  };
  const saveAmount = () => {
    if (commissionType === "flat") {
      const finalValue = +initialPrice - +commissionValue;

      const commissionValueInInt = +commissionValue;
      // setCommission(commissionValue);
      setPrice({
        ...price,
        discount_price: finalValue,
        discount_amount: +commissionValueInInt.toFixed(2),
        discount_type: "flat",
      });
    } else {
      const finalValue =
        +initialPrice - (+initialPrice * +commissionValue) / 100;

      const com = (+initialPrice - +finalValue).toFixed(2);
      // setCommission(`${com}`);
      setPrice({
        ...price,
        discount_price: finalValue,
        discount_amount: +com,
        discount_type: "percentage",
      });
    }
    setShowInputForm(!showInputForm);
  };

  const handleRelatedPartiesSubmit = () => {
    if (!relatedPropertiesLead || !relatedPropertiesRole) {
      alert("Please enter both Lead and Role before adding.");
      return;
    }

    const newParty: RelatedParty = {
      lead: relatedPropertiesLead,
      role: relatedPropertiesRole,
    };

    setRelatedParties((prev) => [...prev, newParty]);

    // Reset input fields after submission
    setRelatedPartiesLead({ label: "", value: "" });
    setRelatedPartiesRole("");
  };

  // const handleSelectCity = (e: any) => {
  //   const filterState = usaCityStateListData.find((item) => item.state === e);
  //   setLeadCity(filterState?.cities);
  // };

  const handleDeleteRelatedParty = (id: string) => {
    const updatedParties = relatedParties.filter(
      (party: any) => party.id !== id
    );
    setRelatedParties(updatedParties);
  };

  return (
    <Modal
      size="xl"
      show={show}
      centered
      backdrop="static"
      onHide={handleClose}
      contentClassName="bg-body-highlight p-6 border border-translucent"
    >
      <Modal.Header className="border-0 p-0 mb-2">
        <h3 className="mb-0">Update Deal Information</h3>
        <Button
          variant="phoenix-secondary"
          className="ms-auto"
          onClick={handleClose}
          size="sm"
        >
          <FontAwesomeIcon icon={faTimes} className="text-danger" />
        </Button>
      </Modal.Header>
      <Modal.Body className="px-0 mb-6">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Col xl={12}>
            <Row className="g-3">
              <Col lg={6}>
                <Form.Group>
                  <Form.Label>Deal Name</Form.Label>
                  <Form.Control
                    {...register("deal_name", { required: true })}
                    type="text"
                    placeholder="Enter Name"
                  />
                  {errors.deal_name?.type === "required" && (
                    <p role="alert" className="text-danger fs-9 pt-1 mb-0">
                      Deal Name is Required
                    </p>
                  )}
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group>
                  <Form.Label>Customers</Form.Label>
                  <ReactSelect
                    options={leads}
                    value={selectedLeads}
                    // onChange={(e: any) => setCustomerName(e.target.value)}
                    onChange={(selectedOptions: any) =>
                      setSelectedLeads(selectedOptions)
                    }
                    isMulti
                    placeholder=""
                    styles={{
                      // @ts-ignore
                      control: (baseStyles) => ({
                        ...baseStyles,
                        height: "36px",
                        padding: "0px",
                        fontSize: "0.8rem",
                        alignItems: "start",
                      }),
                      // @ts-ignore
                      dropdownIndicator: (baseStyles) => ({
                        ...baseStyles,
                        display: "none",
                      }),
                    }}
                  />
                </Form.Group>
              </Col>
              <Col lg={12}>
                <Row className="g-3">
                  {/* <Form.Group as={Col} xs={5}>
                    <Form.Label>State</Form.Label>
                    <Form.Select
                      value={state}
                      onChange={(e) => handleSelectCity(e.target.value)}
                    >
                      <option value="" disabled selected>
                        Select State
                      </option>
                      {usaCityStateListData.map((item: any, i: any) => (
                        <option key={i} value={item.state}>
                          {item.state}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group as={Col} xs={4}>
                    <Form.Label>City</Form.Label>
                    <Form.Select
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                    >
                      <option value="" selected disabled>
                        Select City
                      </option>
                      {leadCity.map((item: any, i: any) => (
                        <option key={i} value={item}>
                          {item}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group> */}
                  <Form.Group as={Col} xs={5}>
                    <Form.Label>State</Form.Label>
                    <Form.Control
                      {...register("deal_state")}
                      type="text"
                      placeholder="Enter State Name"
                    />
                  </Form.Group>
                  <Form.Group as={Col} xs={4}>
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      {...register("deal_city")}
                      type="text"
                      placeholder="Enter City Name"
                    />
                  </Form.Group>

                  <Form.Group as={Col} xs={3}>
                    <Form.Group>
                      <Form.Label>Zip</Form.Label>
                      <Form.Control
                        {...register("deal_zip")}
                        type="text"
                        placeholder="ZIP Code"
                      />
                    </Form.Group>
                  </Form.Group>
                </Row>
              </Col>

              <Col lg={6}>
                <Form.Group>
                  <Form.Label>Price</Form.Label>
                  <InputGroup>
                    <InputGroup.Text id="basic-addon1">$</InputGroup.Text>
                    <Form.Control
                      {...register("deal_price")}
                      type="number"
                      placeholder="Amount"
                      aria-label="Amount"
                      aria-describedby="basic-addon1"
                      onChange={(e) => setInitialPrice(e.target.value)}
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group>
                  <Form.Label>Commission</Form.Label>

                  <Row className="g-2">
                    <Col xs={5}>
                      <ButtonGroup className="w-100">
                        {radios.map((radio, idx) => (
                          <ToggleButton
                            className="w-50"
                            key={radio.value}
                            id={`radio-${idx}`}
                            type="radio"
                            // variant="phoenix-primary"
                            variant={
                              idx % 2 ? "phoenix-warning" : "phoenix-info"
                            }
                            name="radio"
                            value={radio.value}
                            checked={commissionType === radio.value}
                            onChange={(e) =>
                              setCommissionType(e.currentTarget.value)
                            }
                            disabled={!showInputForm}
                          >
                            {radio.name}
                          </ToggleButton>
                        ))}
                      </ButtonGroup>
                    </Col>
                    <Col xs={5}>
                      {/* {!showInputForm && (
                        <Form.Control
                          type="number"
                          placeholder="Amount"
                          value={price.discount_amount}
                          disabled
                        />
                      )}
                      {showInputForm && (
                        <Form.Control
                          type="number"
                          placeholder="Amount"
                          value={commissionValue}
                          onChange={(e) =>
                            handleCommissionValue(e.target.value)
                          }
                        />
                      )} */}
                      <Form.Control
                        type="number"
                        placeholder="Amount"
                        value={
                          showInputForm
                            ? commissionValue
                            : price.discount_amount
                        }
                        disabled={!showInputForm}
                        onChange={(e) => handleCommissionValue(e.target.value)}
                      />
                    </Col>
                    <Col xs={2}>
                      {showInputForm ? (
                        <Button
                          variant="phoenix-success"
                          className="w-100"
                          onClick={saveAmount}
                        >
                          <FontAwesomeIcon icon={faSave} />
                        </Button>
                      ) : (
                        <Button
                          variant="phoenix-warning"
                          className="w-100"
                          onClick={() => setShowInputForm(true)}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                      )}
                    </Col>
                  </Row>
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Label>MLS ID</Form.Label>
                <Form.Control
                  {...register("deal_mls_id")}
                  type="text"
                  placeholder="MLS ID"
                />
              </Col>
              <Col lg={6}>
                <Form.Group>
                  <Form.Label>Collaborator</Form.Label>
                  <ReactSelect
                    options={collaborators}
                    value={selectedCollaborators}
                    onChange={(selectedOptions: any) =>
                      setSelectedCollaborators(selectedOptions)
                    }
                    isMulti
                    placeholder=""
                    styles={{
                      // @ts-ignore
                      control: (baseStyles) => ({
                        ...baseStyles,
                        height: "36px",
                        padding: "0px",
                        fontSize: "0.8rem",
                        alignItems: "start",
                      }),
                      // @ts-ignore
                      dropdownIndicator: (baseStyles) => ({
                        ...baseStyles,
                        display: "none",
                      }),
                    }}
                  />
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Row className="g-3">
                  <Form.Group as={Col} xs={6}>
                    <Form.Label>
                      Lead Type <span className="text-danger"> *</span>
                    </Form.Label>
                    <Form.Select
                      value={leadType}
                      onChange={(e) => matchStatus(e.target.value)}
                    >
                      <option value="" disabled selected>
                        Select
                      </option>
                      {leadTypes.map((item: any, i: any) => (
                        <option key={i} value={item.id}>
                          {item.lead_type}
                        </option>
                      ))}
                    </Form.Select>
                    {typeError && (
                      <p role="alert" className="text-danger fs-9 pt-1 mb-0">
                        Lead Type is Required
                      </p>
                    )}
                  </Form.Group>
                  <Form.Group as={Col} xs={6}>
                    <Form.Label>
                      Status <span className="text-danger"> *</span>
                    </Form.Label>
                    <Form.Select
                      value={leadStatus}
                      className="form-select"
                      onChange={(e) => handleSelectStatus(e.target.value)}
                    >
                      <option value="" disabled selected>
                        Select
                      </option>
                      {selectedLeadStatus.map((item: any, i: any) => (
                        <option key={i} value={item.id}>
                          {item.status}
                        </option>
                      ))}
                    </Form.Select>
                    {statusError && (
                      <p role="alert" className="text-danger fs-9 pt-1 mb-0">
                        Status is Required
                      </p>
                    )}
                  </Form.Group>
                </Row>
              </Col>

              <Col lg={6}>
                <Row className="g-3">
                  <Form.Group as={Col} xs={6}>
                    <Form.Label>Closing Date</Form.Label>
                    <Form.Control
                      {...register("deal_closing_date")}
                      type="date"
                      placeholder="Closing Date"
                    />
                  </Form.Group>

                  <Form.Group as={Col} xs={6}>
                    <Form.Label>Inspection Period</Form.Label>
                    <Form.Control
                      type="number"
                      defaultValue="10"
                      placeholder="Inspection Period"
                      {...register("deal_inspection_period")}
                    />
                  </Form.Group>
                </Row>
              </Col>

              <Col lg={6}>
                <Form.Group>
                  <Form.Label>Deal Notes</Form.Label>
                  <Form.Control
                    {...register("deal_note")}
                    as="textarea"
                    rows={3}
                  />
                </Form.Group>
              </Col>
              <Col lg={6}>
                <Form.Group>
                  <Form.Label>
                    Select Stage <span className="text-danger"> *</span>
                  </Form.Label>
                  <Form.Select
                    className="form-select"
                    {...register("deal_stage_id", {
                      required: true,
                    })}
                  >
                    <option value="" disabled>
                      Select Deal Stage
                    </option>
                    {deals?.map((item: any, i: any) => (
                      <option key={i} value={item.id}>
                        {item?.deal_stage_name}
                      </option>
                    ))}
                  </Form.Select>
                  {errors.deal_stage_id?.type === "required" && (
                    <p role="alert" className="text-danger fs-9 pt-1 mb-0">
                      Deal Stage is Required
                    </p>
                  )}
                </Form.Group>
              </Col>
              
              <Col lg={12}>
                <Card className="mt-6">
                  <Card.Body>
                    <Form.Label className="form-label-header mb-2 fs-6">
                      Related Parties
                    </Form.Label>
                    <div className="border p-3 rounded-2 d-flex gap-3">
                      <div className="w-50">
                        <Form.Group>
                          <Form.Label>Name</Form.Label>
                          <ReactSelect
                            options={leads}
                            onChange={(selectedOptions: any) =>
                              setRelatedPartiesLead(selectedOptions)
                            }
                            placeholder="Select Agent"
                            styles={{
                              // @ts-ignore
                              control: (baseStyles) => ({
                                ...baseStyles,
                                height: "36px",
                                fontSize: "0.8rem",
                                alignItems: "start",
                              }),
                              // @ts-ignore
                              dropdownIndicator: (baseStyles) => ({
                                ...baseStyles,
                                padding: "18px",
                                display: "none",
                              }),
                              // @ts-ignore
                              multiValueLabel: (styles) => ({
                                ...styles,
                                padding: "10px",
                              }),
                            }}
                            // value={selectedOption}
                          />
                        </Form.Group>
                      </div>
                      <div className="w-25">
                        <Form.Group>
                          <Form.Label>Assigned Role</Form.Label>
                          <Form.Select
                            className="form-select"
                            onChange={(e) => setRelatedPartiesRole(e.target.value)}
                            // onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                            //   setRelatedPartiesRole(e.target.value)
                            // }
                          >
                            <option selected>Select</option>
                            <option>Buyers Agent</option>
                            <option>Listing Agent</option>
                            <option>Transaction Coordinator</option>
                          </Form.Select>
                        </Form.Group>
                      </div>
                      <div className="w-25">
                        <Button
                          variant="outline-primary"
                          onClick={handleRelatedPartiesSubmit}
                          className="w-100 mt-4"
                        >
                          Add
                        </Button>
                      </div>
                    </div>

                    {relatedParties?.map((item: any, i: any) => (
                      <div className="mt-3 d-flex gap-6 align-items-center" key={i}>
                        <div className="d-flex align-items-center gap-2 ">
                          <h4 className="mb-0">
                            {item?.lead?.lead_first_name
                              ? item?.lead?.lead_first_name
                              : item?.lead?.label}
                          </h4>

                          <p className="mb-0">({item.role})</p>
                        </div>

                        <Button
                          variant="phoenix-danger"
                          size="sm"
                          onClick={() => handleDeleteRelatedParty(item.id)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </div>
                    ))}
                  </Card.Body>
                </Card>
              </Col>
              
              <Col lg={12}>
                <div className="text-end">
                  <Button
                    variant="link"
                    className="text-danger px-3 mt-6"
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    className="mt-6"
                    disabled={loading}
                  >
                    {loading ? "Updating..." : "Update Deal"}
                  </Button>
                </div>
              </Col>
            </Row>
          </Col>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default EditDealModal;
